/**
 * Auth service — all Supabase auth calls live here.
 * Class-based singleton per architecture rules.
 */
import { Platform } from 'react-native'
import * as Device from 'expo-device'
import type { Session } from '@supabase/supabase-js'
import { supabase } from '../supabase'
import { env } from '../env'
import {
  isInvalidRefreshTokenError,
  isOfflineNetworkError,
  ServiceError,
} from '../errors/service-error'
import { captureError } from '../utils/observability'
// Platform-split Sentry entry: Metro resolves `sentry.web.ts` on web and
// `sentry.ts` on native. Importing `@sentry/react-native` from here (even via
// `require`, which Metro still resolves statically) shipped the native SDK and
// its nested @sentry/core + @sentry/browser copies inside the web bundle.
import { Sentry } from '../utils/sentry'
import { securityTelemetryService } from './security-telemetry.service'
import { productTelemetryService } from './product-telemetry.service'
import { clearAllSecureStorage } from '../storage/secure-storage'
import type { UserProfile } from '../types/auth.types'
import type { RegisterInput, LoginInput } from '../validations/auth.schema'
import { mapProfile } from './profile.utils'

type AuthOperationError = {
  message: string
  code?: string
  status?: number
  cause?: unknown
}

type AuthOperationResult = {
  error: AuthOperationError | null
}

const AUTH_RETRY_DELAY_MS = 850

/**
 * Supabase's auth client already routes through the 15s `fetchWithTimeout`
 * adapter, but a quiet mobile handoff can eat that budget, then the one
 * transient retry, without the user ever learning why the button sat in
 * `loading`. Registration receives no other feedback loop (no websocket, no
 * collision detector) so a stuck promise reads as "infinite spinner".
 *
 * `signUp` gets its own finite budget ABOVE the single-fetch ceiling: at 20s
 * the user sees a deterministic "El servidor tardó en responder" instead of
 * watching a spinner that may still need another 10s of retry. The underlying
 * request is left to finish in the background — if it created the user, the
 * user's next attempt hits the already-mapped duplicate error, which is the
 * correct recovery either way.
 */
const SIGN_UP_TIMEOUT_MS = 20_000

function raceWithBudget<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(
        new ServiceError({
          message: `Operation did not settle within ${timeoutMs}ms`,
          code: 'OPERATION_TIMED_OUT',
          statusCode: 504,
          userMessageKey: 'errors.timeout',
        }),
      )
    }, timeoutMs)
    promise.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      (error) => {
        clearTimeout(timer)
        reject(error)
      },
    )
  })
}

function authErrorText(error: unknown, seen = new Set<unknown>()): string {
  if (!error || seen.has(error)) return ''
  seen.add(error)

  if (typeof error === 'string') return error
  if (typeof error !== 'object') return String(error)

  const item = error as {
    name?: unknown
    message?: unknown
    code?: unknown
    status?: unknown
    cause?: unknown
  }
  return [item.name, item.message, item.code, item.status, authErrorText(item.cause, seen)]
    .filter(
      (value): value is string | number => typeof value === 'string' || typeof value === 'number',
    )
    .map(String)
    .join('\n')
}

function normalizeAuthError(error: unknown): AuthOperationError {
  if (error && typeof error === 'object') {
    const item = error as { message?: unknown; code?: unknown; status?: unknown; cause?: unknown }
    return {
      message:
        typeof item.message === 'string' ? item.message : authErrorText(error) || 'Auth failed',
      code: typeof item.code === 'string' ? item.code : undefined,
      status: typeof item.status === 'number' ? item.status : undefined,
      cause: item.cause,
    }
  }

  return {
    message: authErrorText(error) || 'Auth failed',
    cause: error,
  }
}

function isInvalidPasswordAuthError(error: AuthOperationError): boolean {
  return /invalid login credentials|invalid_grant|invalid_credentials|auth\.errors\.invalid_credentials/i.test(
    authErrorText(error),
  )
}

function isAuthRateLimited(error: AuthOperationError): boolean {
  return error.status === 429 || /rate limit|too many requests/i.test(authErrorText(error))
}

function isAuthServiceUnavailable(error: AuthOperationError): boolean {
  const statusCode = error.status ?? 0
  return (
    statusCode === 402 ||
    statusCode === 408 ||
    statusCode >= 500 ||
    /exceed_cached_egress_quota|service .*restricted|service.*unavailable|quota|failed to fetch|network request failed|load failed|timeout|aborted|temporar/i.test(
      authErrorText(error),
    )
  )
}

function shouldRetryAuthOperation(error: AuthOperationError): boolean {
  if (error.status === 402 || isAuthRateLimited(error) || isInvalidPasswordAuthError(error)) {
    return false
  }
  if (/OPERATION_TIMED_OUT|did not settle within/i.test(authErrorText(error))) {
    return false
  }

  const statusCode = error.status ?? 0
  return (
    statusCode === 408 ||
    statusCode >= 500 ||
    /failed to fetch|network request failed|load failed|timeout|aborted|unexpected_failure|internal_server_error|internal server error/i.test(
      authErrorText(error),
    )
  )
}

async function waitForAuthRetry(): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, AUTH_RETRY_DELAY_MS))
}

async function runAuthOperationWithTransientRetry<T extends AuthOperationResult>(
  operation: () => Promise<T>,
): Promise<T> {
  let firstResult: T
  try {
    firstResult = await operation()
  } catch (error) {
    const normalized = normalizeAuthError(error)
    if (!shouldRetryAuthOperation(normalized)) throw error
    await waitForAuthRetry()
    return operation()
  }

  if (firstResult.error && shouldRetryAuthOperation(firstResult.error)) {
    await waitForAuthRetry()
    return operation()
  }

  return firstResult
}

function mapPasswordSignInError(error: AuthOperationError) {
  const statusCode = error.status ?? 401
  if (statusCode === 429) {
    return new ServiceError({
      message: error.message,
      code: error.code ?? 'AUTH_RATE_LIMITED',
      statusCode,
      userMessageKey: 'errors.rate_limited',
      cause: error,
    })
  }

  const isInvalidCredentials = isInvalidPasswordAuthError(error)
  if (isInvalidCredentials) {
    return new ServiceError({
      message: 'auth.errors.invalid_credentials',
      code: 'AUTH_INVALID_CREDENTIALS',
      statusCode,
      userMessageKey: 'auth.errors.invalid_credentials',
      cause: error,
    })
  }

  if (isOfflineNetworkError(error)) {
    return new ServiceError({
      message: error.message,
      code: 'AUTH_NETWORK_ERROR',
      statusCode: 503,
      userMessageKey: 'auth.errors.network',
      cause: error,
    })
  }

  return new ServiceError({
    message: error.message,
    code: error.code ?? 'AUTH_SERVICE_UNAVAILABLE',
    statusCode,
    userMessageKey: 'auth.errors.service_unavailable',
    cause: error,
  })
}

function mapPasswordResetError(error: AuthOperationError): ServiceError | null {
  if (isAuthRateLimited(error)) {
    return new ServiceError({
      message: error.message,
      code: error.code ?? 'AUTH_RATE_LIMITED',
      statusCode: error.status ?? 429,
      userMessageKey: 'errors.rate_limited',
      cause: error,
    })
  }

  if (isAuthServiceUnavailable(error)) {
    return new ServiceError({
      message: error.message,
      code: error.code ?? 'AUTH_SERVICE_UNAVAILABLE',
      statusCode: error.status ?? 503,
      userMessageKey: 'auth.errors.service_unavailable',
      cause: error,
    })
  }

  return null
}

function edgeFunctionUserMessageKey(status: number, message: string): string | null {
  const normalizedMessage = message.toLowerCase()

  if (status === 409 && normalizedMessage.includes('phone')) return 'auth.errors.phone_in_use'
  if (status === 409 && normalizedMessage.includes('email')) return 'auth.errors.email_in_use'
  if (status === 409) return 'errors.unique_violation'
  if (status === 429) return 'errors.rate_limited'
  if (status === 422) return 'errors.validation'
  if (status === 403) return 'errors.forbidden'
  return null
}

/**
 * First and last name out of a Google ID token, in the order Google fills them.
 *
 * The token carries `given_name`/`family_name` for most accounts and only a
 * single `name` (or `full_name`) for some — a Workspace account with one
 * display field, or a locale where the split is not made. Splitting on the
 * first space is a compromise and it is the right one here: the alternative is
 * an empty first name, which is what this replaces.
 */
function googleNameClaims(claims: Record<string, unknown>): [string, string] {
  const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')

  const given = text(claims.given_name)
  const family = text(claims.family_name)
  if (given) return [given, family]

  const whole = text(claims.name) || text(claims.full_name)
  if (!whole) return ['', '']
  const [first, ...rest] = whole.split(/\s+/)
  return [first ?? '', rest.join(' ')]
}

/** Decode JWT payload without verifying — diagnostics only (no PII values). */
function peekIdTokenClaims(token: string): {
  aud: string | null
  azp: string | null
  iss: string | null
  hasMail: boolean
  mailOk: boolean | null
} {
  try {
    const part = token.split('.')[1]
    if (!part) return { aud: null, azp: null, iss: null, hasMail: false, mailOk: null }
    const padded = part.replace(/-/g, '+').replace(/_/g, '/')
    const json =
      typeof globalThis.atob === 'function'
        ? globalThis.atob(padded)
        : Buffer.from(padded, 'base64').toString('utf8')
    const payload = JSON.parse(json) as Record<string, unknown>
    const audRaw = payload.aud
    const aud =
      typeof audRaw === 'string' ? audRaw : Array.isArray(audRaw) ? String(audRaw[0] ?? '') : null
    return {
      aud: aud || null,
      azp: typeof payload.azp === 'string' ? payload.azp : null,
      iss: typeof payload.iss === 'string' ? payload.iss : null,
      // Keys avoid product-analytics redaction of *email* field names.
      hasMail: typeof payload.email === 'string' && payload.email.length > 0,
      mailOk:
        typeof payload.email_verified === 'boolean'
          ? payload.email_verified
          : payload.email_verified === 'true'
            ? true
            : payload.email_verified === 'false'
              ? false
              : null,
    }
  } catch {
    return { aud: null, azp: null, iss: null, hasMail: false, mailOk: null }
  }
}

function authErrorHaystack(error: unknown): string {
  if (!error || typeof error !== 'object') return String(error ?? '')
  const e = error as Record<string, unknown>
  return [e.message, e.code, e.error, e.error_code, e.error_description, e.msg, e.status]
    .filter((v) => typeof v === 'string' || typeof v === 'number')
    .map(String)
    .join('\n')
}

class AuthServiceImpl {
  // --- Auth operations ---

  async signUp(input: RegisterInput): Promise<{ userId: string }> {
    // Supabase only allows email OR phone at signup — not both.
    // Zero-friction email path: email + password only. Names/DOB/phone are
    // progressive profile fields (Google provides names; phone/DOB at booking
    // or verification hub — never re-asked if already present).
    const { data, error } = await raceWithBudget(
      runAuthOperationWithTransientRetry(() =>
        supabase.auth.signUp({
          email: input.email,
          password: input.password,
          options: {
            data: {
              preferred_locale: input.preferredLocale ?? 'es',
              tago_accepted_terms: true,
            },
          },
        }),
      ),
      SIGN_UP_TIMEOUT_MS,
    )

    if (error) {
      throw ServiceError.fromSupabaseError(error)
    }

    if (!data.user) {
      throw new ServiceError({
        message: 'Signup failed',
        code: 'SIGNUP_FAILED',
        statusCode: 500,
      })
    }

    return { userId: data.user.id }
  }

  async signInWithPassword(input: LoginInput): Promise<UserProfile | null> {
    let result: Awaited<ReturnType<typeof supabase.auth.signInWithPassword>>
    try {
      result = await runAuthOperationWithTransientRetry(() =>
        supabase.auth.signInWithPassword({
          email: input.email,
          password: input.password,
        }),
      )
    } catch (error) {
      throw mapPasswordSignInError(normalizeAuthError(error))
    }

    const { data, error } = result

    if (error) {
      // NEVER reveal if email exists. 4xx auth failures stay generic, but
      // backend/provider outages must not masquerade as a bad password.
      throw mapPasswordSignInError(error)
    }

    this.setSentryUser(data.user.id, data.user.email)
    // Authentication and profile hydration are intentionally decoupled.
    // Supabase has already issued a valid session here; blocking login on a
    // profile SELECT makes transient trigger/RLS/profile-cache failures look
    // like "login does nothing". The global profile query hydrates and retries
    // after SIGNED_IN, while AuthGate can route the user immediately.
    return null
  }

  async signInWithGoogle(): Promise<UserProfile | null> {
    if (Platform.OS === 'web') {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
        },
      })
      if (error) throw ServiceError.fromSupabaseError(error)
      return null
    }

    // Native must stay native. Opening Supabase hosted OAuth on Android/iOS
    // exposes the project ref in Google's account chooser and breaks the
    // app-trust model. If native Google is misconfigured, fail loudly with a
    // Tago-controlled error so package/SHA/client-id config gets fixed at the
    // source instead of leaking users into a browser fallback.
    return this.signInWithGoogleNativeSdk()
  }

  private recordGoogleAuthEvent(ok: boolean, stage: string, extra?: Record<string, unknown>): void {
    productTelemetryService.recordEvent(
      'form_submit',
      ok ? 'auth.google_sign_in' : 'auth.google_sign_in_failed',
      {
        routePath: '/auth/google',
        screenName: 'auth.google',
        elementId: 'auth.google-sign-in',
        elementType: 'auth_step',
        metadata: {
          ok,
          stage,
          provider: 'google',
          platform: Platform.OS,
          ...extra,
        },
      },
    )
  }

  private async signInWithGoogleNativeSdk(): Promise<UserProfile | null> {
    const { GoogleSignin } = await import('@react-native-google-signin/google-signin')

    if (!env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID) {
      this.recordGoogleAuthEvent(false, 'not_configured')
      throw new ServiceError({
        message: 'Google sign-in is not configured',
        code: 'GOOGLE_AUTH_NOT_CONFIGURED',
        statusCode: 500,
        userMessageKey: 'auth.errors.google_not_configured',
      })
    }

    // webClientId MUST be the Web OAuth client so the ID token `aud` matches
    // Supabase Auth. iosClientId is only the iOS SDK surface. Keep configure
    // minimal — extra scopes/offlineAccess have crashed some Play builds.
    GoogleSignin.configure({
      webClientId: env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID,
      ...(env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID
        ? { iosClientId: env.EXPO_PUBLIC_GOOGLE_IOS_CLIENT_ID }
        : {}),
    })

    let response: Awaited<ReturnType<typeof GoogleSignin.signIn>>
    try {
      if (Platform.OS === 'android') {
        await GoogleSignin.hasPlayServices({ showPlayServicesUpdateDialog: true })
      }
      // Prefer account chooser when a previous Google session exists, but never
      // let signOut take down the process (some OEM builds crash here).
      try {
        if (GoogleSignin.hasPreviousSignIn()) {
          await GoogleSignin.signOut()
        }
      } catch {
        // ignore — signIn still presents the chooser
      }
      response = await GoogleSignin.signIn()
    } catch (err: unknown) {
      const code = String((err as { code?: string | number }).code ?? '')
      const message = String((err as { message?: string }).message ?? '')
      const haystack = `${code} ${message}`.toLowerCase()
      if (
        haystack.includes('sign_in_cancelled') ||
        haystack.includes('canceled') ||
        haystack.includes('cancelled') ||
        code === '12501' // Google Sign-In cancelled
      ) {
        this.recordGoogleAuthEvent(false, 'cancelled', { googleErrorCode: code || '12501' })
        return null
      }
      // 12500 = sign-in failed (often OAuth consent Testing / access blocked)
      // 10 = DEVELOPER_ERROR (SHA-1 / package / client id mismatch)
      // 7 = NETWORK_ERROR
      const isAccessBlocked =
        code === '12500' ||
        haystack.includes('access_denied') ||
        haystack.includes('access blocked') ||
        haystack.includes('disallowed_useragent') ||
        haystack.includes('403')
      const isDeveloperConfig =
        code === '10' ||
        haystack.includes('developer_error') ||
        haystack.includes('apiexception: 10')
      const isInternalProviderError =
        code === '8' || haystack.includes('internal_error') || haystack.includes('api exception: 8')
      const isPlayServicesError =
        isInternalProviderError ||
        haystack.includes('play_services') ||
        haystack.includes('play services') ||
        haystack.includes('google play')
      const isNetwork = code === '7' || haystack.includes('network') || haystack.includes('timeout')

      const serviceCode = isDeveloperConfig
        ? 'GOOGLE_AUTH_NOT_CONFIGURED'
        : isInternalProviderError
          ? 'GOOGLE_INTERNAL_ERROR'
          : isPlayServicesError
            ? 'GOOGLE_PLAY_SERVICES_UNAVAILABLE'
            : isNetwork
              ? 'GOOGLE_NETWORK_ERROR'
              : isAccessBlocked
                ? 'GOOGLE_ACCESS_BLOCKED'
                : 'GOOGLE_NATIVE_AUTH_FAILED'

      this.recordGoogleAuthEvent(false, 'native_sdk', {
        googleErrorCode: code || 'unknown',
        serviceCode,
        message: message.slice(0, 160),
      })
      // Actual missing Play Services/network keep their recovery UX. INTERNAL_ERROR
      // is not a capability test: preserve and report that provider failure,
      // then throw the same user-facing recoverable ServiceError.
      if (isInternalProviderError || (!isPlayServicesError && !isNetwork)) {
        captureError(err, {
          screen: 'auth.service',
          action: 'google-native-sdk',
          googleErrorCode: code || 'unknown',
          serviceCode,
        })
      }

      throw new ServiceError({
        message: `Native Google sign-in failed${code ? ` (${code})` : ''}: ${message}`.trim(),
        code: serviceCode,
        statusCode: isNetwork ? 503 : 401,
        // INTERNAL_ERROR (8) NO es un problema de Play Services del usuario:
        // es el proveedor fallando (p. ej. cliente OAuth Android sin el SHA-1
        // de Play App Signing). Mandarlo a "actualizá Play Services" era una
        // instrucción que no arregla nada — TAGO-MOBILE-5D, tres intentos del
        // dueño en su Redmi, build 349.
        userMessageKey: isDeveloperConfig
          ? 'auth.errors.google_not_configured'
          : isInternalProviderError
            ? 'auth.errors.google_internal'
            : isPlayServicesError
              ? 'auth.errors.google_play_services'
              : isNetwork
                ? 'auth.errors.network'
                : isAccessBlocked
                  ? 'auth.errors.google_access_blocked'
                  : 'auth.errors.google_failed',
        cause: err,
      })
    }

    if (response.type === 'cancelled') {
      this.recordGoogleAuthEvent(false, 'cancelled')
      return null
    }

    // v13: data is User { idToken, user, scopes }. Prefer idToken from the
    // sign-in payload; only call getTokens() as a last resort (it has crashed
    // on some Android builds when called immediately after signIn).
    let idToken: string | null =
      typeof response.data?.idToken === 'string' && response.data.idToken.length > 0
        ? response.data.idToken
        : null
    if (!idToken) {
      try {
        const tokens = await GoogleSignin.getTokens()
        idToken = tokens.idToken || null
      } catch (tokenErr) {
        this.recordGoogleAuthEvent(false, 'get_tokens', {
          message: String((tokenErr as Error)?.message ?? tokenErr).slice(0, 160),
        })
        captureError(tokenErr, { screen: 'auth.service', action: 'google-get-tokens' })
      }
    }

    if (!idToken) {
      this.recordGoogleAuthEvent(false, 'id_token_missing', {
        hasUser: response.data?.user ? '1' : '0',
      })
      captureError(new Error('Google sign-in missing idToken'), {
        screen: 'auth.service',
        action: 'google-native-id-token-missing',
        hasUser: response.data?.user ? '1' : '0',
      })
      throw new ServiceError({
        message: 'Native Google sign-in did not return an idToken',
        code: 'GOOGLE_NATIVE_ID_TOKEN_MISSING',
        statusCode: 401,
        userMessageKey: 'auth.errors.google_access_blocked',
      })
    }

    // Diagnostics only — never log the full token. `aud` tells us if Supabase
    // will reject the token (client id not in Auth Google allowlist).
    const tokenPeek = peekIdTokenClaims(idToken)
    const expectedWebAud = env.EXPO_PUBLIC_GOOGLE_WEB_CLIENT_ID
    const audMismatch = Boolean(tokenPeek.aud && expectedWebAud && tokenPeek.aud !== expectedWebAud)

    const exchangeIdToken = () =>
      supabase.auth.signInWithIdToken({
        provider: 'google',
        token: idToken!,
      })

    let { data, error } = await exchangeIdToken()

    // GoTrue sometimes returns a one-shot Internal_Server_Error (JWKS blip or
    // profile-trigger race). One quiet retry recovers real users without a
    // second Google sheet.
    if (error) {
      const firstHay = authErrorHaystack(error).toLowerCase()
      const looksTransient =
        (error.status ?? 0) >= 500 ||
        /internal_server_error|internal server error|unexpected_failure|over_request/i.test(
          firstHay,
        )
      if (looksTransient) {
        await new Promise((r) => setTimeout(r, 450))
        const retry = await exchangeIdToken()
        data = retry.data
        error = retry.error
        if (!error) {
          this.recordGoogleAuthEvent(true, 'signed_in_after_retry', {
            audSuffix: tokenPeek.aud?.slice(-12) ?? null,
          })
        }
      }
    }

    if (error) {
      const status = error.status ?? 401
      const rawCode =
        typeof (error as { code?: unknown }).code === 'string'
          ? (error as { code: string }).code
          : ''
      const haystack = authErrorHaystack(error).toLowerCase()
      const isRateLimited =
        status === 429 || /rate limit|too many requests|over_request_rate/i.test(haystack)
      const isAudience =
        audMismatch ||
        /audience|invalid.*jwt|bad_jwt|invalid.*token|unacceptable aud/i.test(haystack)
      const isSignupDisabled = /signups? not allowed|signup_disabled/i.test(haystack)
      const isBanned = rawCode === 'user_banned' || /user[_ ]is[_ ]banned/i.test(haystack)
      const isDuplicate =
        /email_already_registered|user already registered|already been registered|identity.*exists|duplicate/i.test(
          haystack,
        )
      const isServer =
        status >= 500 ||
        /internal_server_error|internal server error|unexpected_failure/i.test(haystack)

      const serviceCode = isBanned
        ? 'ACCOUNT_RESTRICTED'
        : isRateLimited
          ? 'AUTH_RATE_LIMITED'
          : isDuplicate
            ? 'GOOGLE_EMAIL_ALREADY_REGISTERED'
            : isAudience
              ? 'GOOGLE_AUTH_NOT_CONFIGURED'
              : isServer
                ? 'GOOGLE_SERVER_ERROR'
                : 'GOOGLE_AUTH_FAILED'

      this.recordGoogleAuthEvent(false, 'supabase_id_token', {
        supabaseCode: rawCode || 'none',
        status: String(status),
        serviceCode,
        message: (error.message ?? '').slice(0, 160),
        // Last 20 chars of client ids (enough to distinguish web vs android vs ios)
        audTail: tokenPeek.aud?.slice(-28) ?? null,
        azpTail: tokenPeek.azp?.slice(-28) ?? null,
        webAudTail: expectedWebAud?.slice(-28) ?? null,
        issHost: tokenPeek.iss?.replace(/^https?:\/\//, '').slice(0, 40) ?? null,
        hasMail: tokenPeek.hasMail,
        mailOk: tokenPeek.mailOk,
        audMismatch,
      })
      const skipSentry = isBanned || isRateLimited || isDuplicate || isServer || isSignupDisabled
      if (!skipSentry) {
        captureError(error, {
          screen: 'auth.service',
          action: 'google-signInWithIdToken',
          supabaseCode: rawCode || 'none',
          status: String(status),
          serviceCode,
          audMismatch: audMismatch ? '1' : '0',
        })
      }

      throw new ServiceError({
        message: error.message || serviceCode,
        code: serviceCode,
        statusCode: status >= 400 ? status : 401,
        userMessageKey: isBanned
          ? 'account_status.suspended_body'
          : isRateLimited
            ? 'errors.rate_limited'
            : isDuplicate
              ? 'auth.errors.google_email_exists'
              : isAudience || isSignupDisabled
                ? 'auth.errors.google_not_configured'
                : isServer
                  ? 'auth.errors.google_server_error'
                  : 'auth.errors.google_failed',
        cause: error,
      })
    }
    if (!data.user) throw ServiceError.unauthorized()

    // Pin the session from the id-token response so welcome/login never race
    // SecureStore and treat a successful Google auth as "cancelled".
    if (data.session?.access_token && data.session.refresh_token) {
      const { error: setErr } = await supabase.auth.setSession({
        access_token: data.session.access_token,
        refresh_token: data.session.refresh_token,
      })
      if (setErr) {
        this.recordGoogleAuthEvent(false, 'set_session', {
          message: (setErr.message ?? '').slice(0, 160),
        })
        // Session may still be live on the client from signInWithIdToken — continue.
        captureError(setErr, { screen: 'auth.service', action: 'google-set-session' })
      }
    }

    // Google hands the name over in the ID token, and this branch used to drop
    // it — so a person who signed in with Google landed in the app with no
    // first name, and the first screen that needed one asked them to type it.
    // The Apple branch above has done this since it was written; the asymmetry
    // was an omission, not a decision.
    //
    // Only when the profile has nothing yet: `first_name` may already hold a
    // correction the person made in their profile, and a provider claim must
    // not overwrite what somebody chose to be called.
    try {
      const claims = (data.user.user_metadata ?? {}) as Record<string, unknown>
      const existingFirstName =
        typeof claims.first_name === 'string' ? claims.first_name.trim() : ''
      if (!existingFirstName) {
        const [givenName, familyName] = googleNameClaims(claims)
        if (givenName) {
          await supabase.auth.updateUser({
            data: { first_name: givenName, last_name: familyName },
          })
        }
      }
    } catch (nameErr) {
      // Never fail login because name metadata write raced.
      captureError(nameErr, { screen: 'auth.service', action: 'google-name-metadata' })
    }

    this.setSentryUser(data.user.id, data.user.email)
    this.recordGoogleAuthEvent(true, 'signed_in', {
      isNewUser: Boolean(
        data.user.created_at && Date.now() - new Date(data.user.created_at).getTime() < 60_000,
      ),
      userIdPrefix: data.user.id.slice(0, 8),
    })
    return null
  }

  async signInWithApple(): Promise<UserProfile | null> {
    // Web goes through Apple's hosted flow, the same shape as Google above.
    //
    // Unlike Google, this cannot be reached until Apple is configured on both
    // sides: a Service ID (a SECOND client id, distinct from the bundle) with
    // app.tago.rent and the Supabase callback registered, and a Sign in with
    // Apple key whose secret Supabase holds. Until then the button is hidden
    // by APPLE_WEB_AUTH_READY in components/auth/SocialAuthRow — a visible
    // button that always fails is worse than no button.
    //
    // Note for whoever configures Supabase: `client_id` must list BOTH the
    // bundle (native id-token flow) and the Service ID (web). Replacing rather
    // than appending silently breaks sign-in on iPhone.
    if (Platform.OS === 'web') {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo: typeof window !== 'undefined' ? window.location.origin : undefined,
        },
      })
      if (error) throw ServiceError.fromSupabaseError(error)
      return null
    }

    // Android has no Apple sign-in surface at all: the button is not rendered
    // there, so reaching this is a bug rather than a user path.
    if (Platform.OS !== 'ios') {
      throw new ServiceError({
        message: 'Apple Sign-In is only available on iOS and web',
        code: 'APPLE_AUTH_UNSUPPORTED_PLATFORM',
        statusCode: 400,
      })
    }

    let credential: Awaited<ReturnType<typeof import('expo-apple-authentication').signInAsync>>
    try {
      const AppleAuth = await import('expo-apple-authentication')
      credential = await AppleAuth.signInAsync({
        requestedScopes: [
          AppleAuth.AppleAuthenticationScope.FULL_NAME,
          AppleAuth.AppleAuthenticationScope.EMAIL,
        ],
      })
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? ''
      const msg = (err as { message?: string }).message ?? ''
      const haystack = `${code} ${msg}`
      if (
        code === 'ERR_CANCELED' ||
        code === 'ERR_REQUEST_CANCELED' ||
        code === '1001' ||
        msg.toLowerCase().includes('cancel')
      ) {
        return null
      }
      const original = haystack.trim() || 'Apple sign-in failed'
      const isUnknown =
        /ERR_REQUEST_UNKNOWN|RequestUnknownException|authorization attempt failed for an unknown reason/i.test(
          haystack,
        )
      const unavailableOnThisDevice = isUnknown && Device.isDevice === false
      throw new ServiceError({
        message: original,
        code: unavailableOnThisDevice ? 'APPLE_AUTH_UNAVAILABLE' : 'APPLE_AUTH_FAILED',
        statusCode: 401,
        userMessageKey: unavailableOnThisDevice
          ? 'auth.errors.apple_unavailable'
          : 'auth.errors.apple_failed',
        cause: err,
      })
    }

    if (!credential.identityToken) {
      throw new ServiceError({
        message: 'Apple sign-in failed: missing identity token',
        code: 'APPLE_AUTH_FAILED',
        statusCode: 401,
        userMessageKey: 'auth.errors.apple_failed',
      })
    }

    const { data, error } = await supabase.auth.signInWithIdToken({
      provider: 'apple',
      token: credential.identityToken,
    })

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!data.user) throw ServiceError.unauthorized()

    // CRITICAL: Apple only provides name on FIRST sign-in ever.
    // Must capture and save immediately or it's lost forever.
    if (credential.fullName?.givenName) {
      await supabase.auth.updateUser({
        data: {
          first_name: credential.fullName.givenName,
          last_name: credential.fullName.familyName ?? '',
        },
      })
    }

    this.setSentryUser(data.user.id, data.user.email)
    return this.getProfile(data.user.id)
  }

  async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut()
    if (error) throw ServiceError.fromSupabaseError(error)
    await clearAllSecureStorage()
    this.clearSentryUser()
  }

  async resetPassword(email: string): Promise<void> {
    // NEVER throw for unknown email — silent success prevents enumeration.
    // Provider outages, rate limits, and quota failures are different:
    // hiding those makes "forgot password did nothing" impossible to debug.
    // Always HTTPS. `tago://reset-password` opened as a blank "Untitled"
    // tab in Gmail/Chrome — custom schemes are not a browser URL. The
    // recovery email itself must also carry token_hash (see recovery.html)
    // so the link works in any client without a PKCE verifier on this device.
    const redirectTo = 'https://app.tago.rent/reset-password'

    let result: Awaited<ReturnType<typeof supabase.auth.resetPasswordForEmail>>
    try {
      result = await runAuthOperationWithTransientRetry(() =>
        supabase.auth.resetPasswordForEmail(email, {
          redirectTo,
        }),
      )
    } catch (err) {
      throw mapPasswordResetError(normalizeAuthError(err)) ?? err
    }

    const { error } = result
    if (error) {
      // Log to Sentry for observability, but don't throw (prevent enumeration)
      captureError(error, { screen: 'auth.service', action: 'reset_password' })
      const mappedError = mapPasswordResetError(error)
      if (mappedError) throw mappedError
    }
  }

  async updatePassword(password: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ password })
    if (error) throw ServiceError.fromSupabaseError(error)
  }

  // --- Contact verification (post-signup — links missing email / phone) ---

  private async authenticatedFunctionHeaders(): Promise<Record<string, string>> {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession()

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!session?.access_token) {
      throw new ServiceError({
        message: 'Auth session missing',
        code: 'AUTH_SESSION_MISSING',
        statusCode: 401,
        userMessageKey: 'auth.errors.session_missing',
      })
    }

    return { Authorization: `Bearer ${session.access_token}` }
  }

  async sendEmailVerification(email: string): Promise<void> {
    const normalized = email.trim().toLowerCase()
    securityTelemetryService.recordContactVerificationEvent({
      event: 'contact_verification_send_requested',
      contactType: 'email',
      flow: 'email_link',
      channel: 'email',
      target: normalized,
    })
    const { error } = await supabase.functions.invoke('request-email-link', {
      body: { email: normalized },
      headers: await this.authenticatedFunctionHeaders(),
    })
    if (error) {
      securityTelemetryService.recordContactVerificationEvent({
        event: 'contact_verification_send_failed',
        contactType: 'email',
        flow: 'email_link',
        channel: 'email',
        target: normalized,
        error,
      })
      throw await this.edgeFunctionError(error)
    }
    securityTelemetryService.recordContactVerificationEvent({
      event: 'contact_verification_sent',
      contactType: 'email',
      flow: 'email_link',
      channel: 'email',
      target: normalized,
    })
  }

  async verifyEmailChange(email: string, token: string): Promise<{ userId: string | null }> {
    const normalized = email.trim().toLowerCase()
    securityTelemetryService.recordContactVerificationEvent({
      event: 'contact_verification_verify_requested',
      contactType: 'email',
      flow: 'email_link',
      channel: 'email',
      target: normalized,
    })
    const { data, error } = await supabase.functions.invoke('verify-email-link', {
      body: {
        email: normalized,
        code: token,
      },
      headers: await this.authenticatedFunctionHeaders(),
    })
    if (error) {
      securityTelemetryService.recordContactVerificationEvent({
        event: 'contact_verification_verify_failed',
        contactType: 'email',
        flow: 'email_link',
        channel: 'email',
        target: normalized,
        error,
      })
      throw await this.edgeFunctionError(error)
    }

    await this.refreshSessionAfterContactChange()
    const userId = typeof data?.userId === 'string' ? data.userId : null
    if (userId) this.setSentryUser(userId, email.trim().toLowerCase())
    securityTelemetryService.recordContactVerificationEvent({
      event: 'contact_verification_verified',
      userId,
      contactType: 'email',
      flow: 'email_link',
      channel: 'email',
      target: normalized,
    })
    return { userId }
  }

  private async refreshSessionAfterContactChange(): Promise<void> {
    const { error } = await supabase.auth.refreshSession()
    if (error && __DEV__) {
      console.warn('[auth.refreshSessionAfterContactChange] non-fatal:', error)
    }
  }

  private async edgeFunctionError(error: unknown): Promise<ServiceError> {
    const message = (await this.readEdgeFunctionErrorBody(error)) ?? 'Edge function failed'
    const statusCode = (error as { context?: Response; status?: number })?.context?.status
    const status = statusCode ?? (error as { status?: number })?.status ?? 500

    return new ServiceError({
      message,
      code: 'EDGE_FUNCTION_ERROR',
      statusCode: status,
      userMessageKey: edgeFunctionUserMessageKey(status, message),
      cause: error,
    })
  }

  private async readEdgeFunctionErrorBody(error: unknown): Promise<string | null> {
    const ctx = (error as { context?: Response }).context
    if (!ctx || typeof ctx.clone !== 'function') return null
    try {
      const body = (await ctx.clone().json()) as { error?: unknown; message?: unknown }
      if (typeof body?.error === 'string' && body.error) return body.error
      if (typeof body?.message === 'string' && body.message) return body.message
    } catch {
      try {
        const text = await ctx.clone().text()
        if (text) return text.slice(0, 200)
      } catch {
        // body unreadable — fall back to the generic message.
      }
    }
    return null
  }

  // --- Session ---

  async getSession(): Promise<Session | null> {
    const { data, error } = await supabase.auth.getSession()
    if (error) {
      if (isInvalidRefreshTokenError(error)) {
        await clearAllSecureStorage()
        return null
      }
      throw ServiceError.fromSupabaseError(error)
    }
    return data.session
  }

  async getProfile(userId: string): Promise<UserProfile> {
    // Own row, via the definer RPC. `profiles` no longer grants column-level
    // SELECT beyond the public subset (display name, avatar, ratings), so
    // `select('*')` — and any select naming a private column — now returns 42501
    // for the caller's own row as much as for anyone else's. A grant cannot make
    // an exception for your own row; current_profile() is that exception, and it
    // resolves the subject from auth.uid() rather than an argument.
    void userId
    const { data, error } = await supabase.rpc('current_profile')

    if (error) throw ServiceError.fromSupabaseError(error)
    if (!data) throw ServiceError.notFound('Profile')

    // Best-effort: backfill the avatar from the OAuth identity provider on
    // first read so Google sign-up users don't see an empty initials avatar
    // while their Google photo sits one user_metadata field away. Safe to
    // run on every fetch — it's a single auth.getUser() call (no DB hit
    // when avatar is already filled) and the actual UPDATE only fires when
    // (a) profile.avatar_url is null AND (b) provider returned a photo URL.
    if (!data.avatar_url) {
      const synced = await this.syncOAuthAvatarIfMissing(userId).catch((err) => {
        if (__DEV__) console.warn('[auth.syncOAuthAvatar] non-fatal:', err)
        return null
      })
      if (synced) {
        return mapProfile({ ...data, avatar_url: synced })
      }
    }

    return mapProfile(data)
  }

  /**
   * Read the current Supabase auth user's metadata, look for an OAuth-issued
   * avatar URL (Google → `picture`, Apple → none, GitHub → `avatar_url`),
   * and write it to `profiles.avatar_url` if the column is currently null.
   * Returns the synced URL or null if nothing to do / no provider photo.
   */
  private async syncOAuthAvatarIfMissing(userId: string): Promise<string | null> {
    const { data: userData } = await supabase.auth.getUser()
    const meta = (userData?.user?.user_metadata ?? {}) as Record<string, unknown>
    // Supabase normalises Google's `picture` claim into both `avatar_url`
    // and `picture` on user_metadata depending on the auth flow path —
    // check both so native (signInWithIdToken) and web (OAuth redirect)
    // both work.
    const photo =
      typeof meta.avatar_url === 'string' && meta.avatar_url
        ? meta.avatar_url
        : typeof meta.picture === 'string' && meta.picture
          ? meta.picture
          : null
    if (!photo) return null
    const { error } = await supabase
      .from('profiles')
      .update({ avatar_url: photo })
      .eq('id', userId)
      .is('avatar_url', null) // only patch when still empty — guards against
    // overwriting a user-uploaded avatar uploaded between this fetch and
    // the update.
    if (error) {
      if (__DEV__) console.warn('[auth.syncOAuthAvatar] update failed:', error)
      return null
    }
    return photo
  }

  // --- Consent & Legal (D.L. 144/2024) ---

  /**
   * Atomic post-signup: legal acceptances + consent records + audit log.
   * Uses a SECURITY DEFINER RPC so it works even without an active session
   * (e.g. when email confirmation is enabled).
   */
  async completeSignup(profileId: string, acceptMarketing: boolean): Promise<void> {
    const { error } = await supabase.rpc('complete_signup', {
      p_profile_id: profileId,
      p_accept_marketing: acceptMarketing,
      p_ip_address: undefined,
      p_user_agent: undefined,
    })
    if (error) throw ServiceError.fromSupabaseError(error)
  }

  // `createLegalAcceptances` and `createConsentRecords` used to live here and
  // were called by nobody: `completeSignup` above does both server-side, in one
  // transaction, with the request IP. Deleted rather than left dormant, because
  // `createConsentRecords` wrote exactly the record this codebase just spent a
  // migration removing — `purpose: c.type` (the column echoing its neighbour
  // instead of saying what the data is for), version '1.0', and no
  // consent_form_url at all. Under D.L. 144/2024 Art. 54 the burden of proving
  // consent is Tago's, and a row with no wording and no URL proves nothing. The
  // next person needing to record a consent would have found this first and
  // reproduced the defect; now the only ways in are `complete_signup` and
  // `complianceService.recordConsent`, and both demand a version and a URL.

  // --- Audit ---

  async auditLog(
    action: string,
    resourceType: string,
    resourceId: string,
    details?: Record<string, unknown>,
    actorId?: string,
  ): Promise<void> {
    const resolvedActorId = actorId ?? (await this.getSession())?.user?.id ?? null
    const { error } = await supabase.from('audit_logs').insert({
      actor_id: resolvedActorId,
      actor_type: 'user',
      action,
      resource_type: resourceType,
      resource_id: resourceId,
      new_values: (details ?? null) as import('../types/database').Json,
    })
    if (error) throw ServiceError.fromSupabaseError(error)
  }

  // --- Listener ---

  onAuthStateChange(callback: (event: string, session: Session | null) => void) {
    return supabase.auth.onAuthStateChange(callback)
  }

  // --- Sentry integration ---

  /**
   * PII minimisation (D.L. 144/2024): only the profile id is attached to the
   * Sentry scope. The `_email` parameter is kept so the six call sites in this
   * service stay untouched, but the value is intentionally discarded — it must
   * never reach Sentry. Same contract as `lib/utils/sentry.ts` and
   * `lib/utils/sentry.web.ts`. Do NOT re-add email/phone/username here.
   */
  private setSentryUser(userId: string, _email?: string | undefined) {
    try {
      Sentry.setUser({ id: userId })
    } catch {
      // Sentry not available
    }
  }

  private clearSentryUser() {
    try {
      Sentry.setUser(null)
    } catch {
      // Sentry not available
    }
  }
}

export const authService = new AuthServiceImpl()
